import { clsx } from 'clsx';
import {
  createContext,
  useContext,
  type ReactNode,
  type RefObject,
} from 'react';

import { Flex, type FlexProps } from '@repo/ui/Flex';

import s from './FloatingContent.module.css';

/* ---- Floating position context ----------------------------------------- */

export interface FloatingPositionContextType {
  arrowRef: RefObject<HTMLSpanElement | null>;
  hasArrow: boolean;
}

const FloatingPositionContext =
  createContext<FloatingPositionContextType | null>(null);

export interface FloatingPositionProviderProps {
  children: ReactNode;
  value: FloatingPositionContextType;
}

export const FloatingPositionProvider = ({
  children,
  value,
}: FloatingPositionProviderProps) => (
  <FloatingPositionContext.Provider value={value}>
    {children}
  </FloatingPositionContext.Provider>
);

export const useFloatingPosition = (): FloatingPositionContextType | null =>
  useContext(FloatingPositionContext);

/* ---- FloatingContent component ----------------------------------------- */

export const variantOptions = ['default', 'tooltip'] as const;
export type Variant = (typeof variantOptions)[number];

export interface FloatingContentProps extends Omit<
  FlexProps,
  'as' | 'padding' | 'gap'
> {
  variant?: Variant;
}

export const FloatingContent = ({
  alignItems = 'flex-start',
  children,
  className,
  direction = 'column',
  variant = 'default',
  ...attrs
}: FloatingContentProps) => {
  const floatingPosition = useFloatingPosition();
  const hasArrow = floatingPosition?.hasArrow ?? false;

  return (
    <Flex
      className={clsx(s.floatingContent, s[`${variant}Variant`], className)}
      data-floating-content=""
      alignItems={alignItems}
      direction={direction}
      {...attrs}
    >
      {hasArrow && (
        <span
          className={s.arrow}
          aria-hidden="true"
          ref={floatingPosition?.arrowRef}
        />
      )}
      {children}
    </Flex>
  );
};
